import {
  arrow,
  autoPlacement,
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  type Placement,
  safePolygon,
  shift,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'
import clsx from 'clsx'
import { motion } from 'motion/react'
import {
  cloneElement,
  type ElementType,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useCallback,
  useRef,
  useState
} from 'react'
import { themeClass } from '~/styles/theme.css'
import { arrowBase, arrowSide, wrapper } from './Tooltip.css'
import { TOOLTIP_OPEN_DELAY } from './TooltipProvider'

type TooltipProps = {
  asChild?: boolean
  as?: ElementType
  direction?: Placement
  text?: ReactNode | null
  html?: string | null
} & PropsWithChildren

export const Tooltip = ({
  asChild = false,
  as: Cmp = 'div', // deprecated, use asChild instead
  direction,
  text,
  html,
  children
}: TooltipProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const middleware = [
    offset(10),
    direction ? flip() : autoPlacement(),
    shift({ padding: 5 }),
    arrow({ element: arrowRef })
  ]

  const { x, y, strategy, refs, context, placement, middlewareData } =
    useFloating({
      placement: direction,
      open: isOpen,
      onOpenChange: setIsOpen,
      middleware,
      whileElementsMounted: autoUpdate
    })

  const { delay: groupDelay, isInstantPhase } = useDelayGroup(context)

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon(),
    // Fallback delay when no TooltipProvider is mounted (group delay is 0)
    delay:
      groupDelay === 0 ? { open: TOOLTIP_OPEN_DELAY, close: 0 } : groupDelay
  })

  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ])

  const staticSide = placement.split('-')[0] as
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'

  // Check if a component can accept refs
  const canAcceptRef = useCallback((element: ReactElement) => {
    // Check if it's a DOM element (string type)
    if (typeof element.type === 'string') {
      return true
    }

    // Check if it's a forwardRef component
    if (typeof element.type === 'object' && element.type) {
      const elementType = element.type as any
      return elementType.$$typeof === Symbol.for('react.forward_ref')
    }

    // Check if it's a class component
    if (
      typeof element.type === 'function' &&
      element.type.prototype?.isReactComponent
    ) {
      return true
    }

    return false
  }, [])

  if (text == null && !html) {
    return children
  }

  const renderTrigger = () => {
    if (asChild) {
      if (!isValidElement(children)) {
        console.warn(
          'Tooltip: when asChild is true, children must be a valid React element',
          children
        )
        return children
      }

      const child = children as ReactElement & { ref?: React.Ref<HTMLElement> }
      const childCanAcceptRef = canAcceptRef(child)
      const referenceProps = getReferenceProps()
      const childRef = child.ref

      const mergedReferenceRef = (node: HTMLElement | null): void => {
        refs.setReference(node)

        if (!childRef) {
          return
        }

        if (typeof childRef === 'function') {
          childRef(node)
          return
        }

        if ('current' in childRef) {
          ;(childRef as React.MutableRefObject<HTMLElement | null>).current =
            node
        }
      }

      // If the child can accept refs, clone it with the ref
      if (childCanAcceptRef) {
        return cloneElement(child, {
          ...referenceProps,
          ref: mergedReferenceRef
        })
      }

      // If the child can't accept refs, wrap it in a span
      return (
        <span ref={refs.setReference} {...referenceProps}>
          {child}
        </span>
      )
    }

    // Fallback to wrapper element
    return (
      <Cmp ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </Cmp>
    )
  }

  return (
    <>
      {renderTrigger()}
      <FloatingPortal>
        {isOpen && (
          <motion.div
            ref={refs.setFloating}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={isInstantPhase ? { duration: 0 } : undefined}
            className={clsx(themeClass, wrapper)}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0
            }}
            {...getFloatingProps()}
          >
            {html != null ? (
              // biome-ignore lint/security/noDangerouslySetInnerHtml: html is explicitly opted-in by the caller for rich content
              <div dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              text
            )}
            <div
              ref={arrowRef}
              className={clsx(arrowBase, arrowSide[staticSide])}
              style={{
                left:
                  middlewareData.arrow?.x != null
                    ? `${middlewareData.arrow.x}px`
                    : '',
                top:
                  middlewareData.arrow?.y != null
                    ? `${middlewareData.arrow.y}px`
                    : ''
              }}
            />
          </motion.div>
        )}
      </FloatingPortal>
    </>
  )
}
